import { useState } from 'react';
import { PageContainer } from '../layout/PageContainer';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
  category: string;
}

const faqs: FAQItem[] = [
  // General
  {
    category: 'General',
    question: 'What is After5PM?',
    answer:
      'After5PM is a browser-based gaming platform featuring classic arcade games like Snake, Falling Blocks, Pill Muncher, and more. Play for free, compete on global leaderboards, and relive the golden age of gaming.',
  },
  {
    category: 'General',
    question: 'Do I need to create an account?',
    answer:
      'No! You can play all free games without an account. However, creating an account lets you save high scores, appear on leaderboards, and sync progress across devices.',
  },
  {
    category: 'General',
    question: 'What devices are supported?',
    answer:
      'After5PM works on any modern web browser—desktop, tablet, or mobile. Games automatically adapt to your screen size and support both keyboard and touch controls.',
  },
  {
    category: 'General',
    question: 'Are the games free?',
    answer:
      'Yes! We offer several classic games completely free. Premium subscribers get access to additional games, global leaderboards, and an ad-free experience.',
  },

  // Account
  {
    category: 'Account',
    question: 'How do I reset my password?',
    answer: (
      <>
        Click &ldquo;Forgot Password&rdquo; on the login page and enter your
        email. You&apos;ll receive a code to reset your password. If you
        don&apos;t receive the email, check your spam folder or contact{' '}
        <a
          href="mailto:support@after5pm.fun"
          className="text-purple-400 hover:text-purple-300"
        >
          support@after5pm.fun
        </a>
        .
      </>
    ),
  },
  {
    category: 'Account',
    question: 'Can I change my username?',
    answer:
      'Currently, usernames cannot be changed after registration. We may add this feature in the future. Choose wisely!',
  },
  {
    category: 'Account',
    question: 'How do I delete my account?',
    answer: (
      <>
        To delete your account and all associated data, email{' '}
        <a
          href="mailto:privacy@after5pm.fun"
          className="text-purple-400 hover:text-purple-300"
        >
          privacy@after5pm.fun
        </a>{' '}
        from your registered email address. We&apos;ll process your request
        within 30 days.
      </>
    ),
  },

  // Subscription
  {
    category: 'Subscription',
    question: "What's included in Premium?",
    answer: (
      <ul className="list-disc pl-5 space-y-1">
        <li>Access to ALL games (current and future)</li>
        <li>Global leaderboards</li>
        <li>Custom themes</li>
        <li>Ad-free experience</li>
        <li>Early access to new games</li>
        <li>Priority support</li>
      </ul>
    ),
  },
  {
    category: 'Subscription',
    question: 'How much does Premium cost?',
    answer:
      'Premium is $4.99/month or $39.99/year (save 33%). You can cancel anytime and keep access until your billing period ends.',
  },
  {
    category: 'Subscription',
    question: 'How do I cancel my subscription?',
    answer: (
      <>
        Go to your{' '}
        <Link to="/profile" className="text-purple-400 hover:text-purple-300">
          Profile
        </Link>{' '}
        page and click &ldquo;Manage Subscription&rdquo; to access the billing
        portal. You can cancel there and your access continues until the end of
        your current billing period.
      </>
    ),
  },
  {
    category: 'Subscription',
    question: 'Do you offer refunds?',
    answer:
      'Due to the digital nature of our service, we generally do not offer refunds. If you experience technical issues preventing access, contact support and we will work with you.',
  },

  // Games
  {
    category: 'Games',
    question: 'What games are available?',
    answer: (
      <>
        Free: Snake, Falling Blocks, Wall Breaker, Pill Muncher, Invaders!
        <br />
        Premium: Blasteroids, Tennis, Flapping Chick, and more coming soon!
      </>
    ),
  },
  {
    category: 'Games',
    question: 'How are high scores calculated?',
    answer:
      'Each game has its own scoring system based on the original arcade rules. Scores are validated server-side to prevent cheating.',
  },
  {
    category: 'Games',
    question: 'My game is lagging. What can I do?',
    answer: (
      <ul className="list-disc pl-5 space-y-1">
        <li>Close other browser tabs</li>
        <li>Disable browser extensions</li>
        <li>Try a different browser (Chrome recommended)</li>
        <li>Check your internet connection</li>
        <li>Clear browser cache and refresh</li>
      </ul>
    ),
  },
  {
    category: 'Games',
    question: "Why didn't my score save?",
    answer:
      "Scores require an active internet connection to save. If you played offline or had connection issues, the score may not have been submitted. Make sure you're logged in for scores to save.",
  },

  // Technical
  {
    category: 'Technical',
    question: 'What browsers are supported?',
    answer:
      'We support the latest versions of Chrome, Firefox, Safari, and Edge. Older browsers may work but are not officially supported.',
  },
  {
    category: 'Technical',
    question: 'Can I play offline?',
    answer:
      'Currently, games require an internet connection. Offline support may be added in the future.',
  },
  {
    category: 'Technical',
    question: 'Is my data secure?',
    answer: (
      <>
        Yes. We use industry-standard encryption, secure authentication via AWS
        Cognito, and never store payment details on our servers. See our{' '}
        <Link to="/privacy" className="text-purple-400 hover:text-purple-300">
          Privacy Policy
        </Link>{' '}
        for details.
      </>
    ),
  },
];

const categories = [...new Set(faqs.map((f) => f.category))];

export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const filteredFaqs =
    activeCategory === 'All'
      ? faqs
      : faqs.filter((f) => f.category === activeCategory);

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-2">
          Frequently Asked Questions
        </h1>
        <p className="text-slate-400 mb-8">
          Can&apos;t find what you&apos;re looking for?{' '}
          <a
            href="mailto:support@after5pm.fun"
            className="text-purple-400 hover:text-purple-300"
          >
            Contact support
          </a>
        </p>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveCategory('All')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === 'All'
                ? 'bg-purple-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ list */}
        <div className="space-y-3">
          {filteredFaqs.map((faq, _index) => {
            const globalIndex = faqs.indexOf(faq);
            const isOpen = openIndex === globalIndex;

            return (
              <div
                key={globalIndex}
                className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  <span
                    className={`text-slate-400 transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  >
                    ▼
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-4 text-slate-300 leading-relaxed border-t border-slate-700/50 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Still need help */}
        <div className="mt-12 bg-slate-800/50 rounded-xl p-8 border border-slate-700 text-center">
          <h2 className="text-xl font-semibold text-white mb-2">
            Still have questions?
          </h2>
          <p className="text-slate-400 mb-4">
            Our support team is here to help.
          </p>
          <a
            href="mailto:support@after5pm.fun"
            className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            📧 Contact Support
          </a>
        </div>
      </div>
    </PageContainer>
  );
}
